/**
 * Statutory 15-minute rounding for NHVR fatigue management.
 *
 * WORK: Round UP to next whole quarter hour (conservative — overstates work)
 * REST: Round DOWN to last whole quarter hour (conservative — understates rest)
 *
 * Rounding applies to DURATION only, never to start/end timestamps.
 * This is a display/reporting layer — compliance always uses actual durations.
 */

const QUARTER_HOUR = 900; // 15 minutes in seconds

/**
 * Round WORK duration UP to the next 15-minute block.
 * e.g. 19 min (1140s) → 30 min (1800s)
 * e.g. 30 min (1800s) → 30 min (1800s) — already on boundary
 * e.g. 0s → 0s
 */
export const roundWorkUp = (seconds) => {
  if (!seconds || seconds <= 0) return 0;
  return Math.ceil(seconds / QUARTER_HOUR) * QUARTER_HOUR;
};

/**
 * Round REST duration DOWN to the last 15-minute block.
 * e.g. 25 min (1500s) → 15 min (900s)
 * e.g. 30 min (1800s) → 30 min (1800s) — already on boundary
 * e.g. 14 min (840s) → 0s
 */
export const roundRestDown = (seconds) => {
  if (!seconds || seconds <= 0) return 0;
  return Math.floor(seconds / QUARTER_HOUR) * QUARTER_HOUR;
};

/**
 * Apply statutory rounding to a segment duration based on type.
 * @param {number} seconds - actual duration in seconds
 * @param {string} segmentType - 'WORK' or 'REST'
 * @returns {number} - rounded duration in seconds
 */
export const applyStatutoryRounding = (seconds, segmentType) => {
  if (segmentType === 'WORK') return roundWorkUp(seconds);
  return roundRestDown(seconds);
};

/**
 * Format a duration in seconds to a human-readable string.
 * @param {number} seconds
 * @returns {string} e.g. "2h 15m", "45m", "0m"
 */
export const formatStatutoryDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '0m';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
};
